/**
 * CEO Command Center - Connection Status Component
 *
 * Displays real-time status of connections in the header.
 * Shows Database (SQLite) and OpenAI connection status with visual indicators.
 *
 * @module components/common/ConnectionStatus
 * @version 11.0.0
 */

import React, { useEffect, useState } from 'react';
import {
  useConnectionStore,
  ConnectionStatus as ConnectionStatusType,
} from '../../stores/connectionStore';
import { useSyncStore } from '../../stores/syncStore';
import { cn } from '../../utils/format';

// ============================================
// STATUS INDICATOR
// ============================================

interface StatusIndicatorProps {
  status: ConnectionStatusType;
  label: string;
  error?: string | null;
  showLabel?: boolean;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  error,
  showLabel = true,
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'connected':
        return 'bg-green-500';
      case 'checking':
        return 'bg-yellow-500 animate-pulse';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'checking':
        return 'Checking...';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 group relative"
      title={error || getStatusText()}
    >
      <span
        className={cn('w-2 h-2 rounded-full transition-colors', getStatusColor())}
      />
      {showLabel && (
        <span
          className={cn(
            'text-xs font-medium transition-colors',
            status === 'connected'
              ? 'text-green-600'
              : status === 'error'
                ? 'text-red-600'
                : status === 'checking'
                  ? 'text-yellow-600'
                  : 'text-gray-500'
          )}
        >
          {label}
        </span>
      )}

      {/* Tooltip on hover for errors */}
      {error && status === 'error' && (
        <div className="absolute top-full left-0 mt-1 hidden group-hover:block z-50">
          <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap max-w-xs">
            {error}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CONNECTION STATUS BAR
// ============================================

interface ConnectionStatusProps {
  className?: string;
  compact?: boolean;
}

/**
 * Connection status display component
 * Shows status of Database (SQLite) and OpenAI connections
 */
export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  className,
  compact = false,
}) => {
  const { database, openai, checkAll } = useConnectionStore();
  const { initialized, initializing } = useSyncStore();
  const [hasChecked, setHasChecked] = useState(false);

  // Wait for sync store initialization before checking connections
  useEffect(() => {
    // Don't check until initialization is complete
    if (initializing) return;

    // Once initialized (or failed), check connections
    if (!hasChecked) {
      // Small delay to ensure state is settled
      const timeout = setTimeout(() => {
        checkAll();
        setHasChecked(true);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [hasChecked, checkAll, initialized, initializing]);

  // Periodic health check every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      checkAll();
    }, 30000);

    return () => clearInterval(interval);
  }, [checkAll]);

  if (compact) {
    // Compact mode: just show dots
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <StatusIndicator
          status={database.status}
          label="Database"
          error={database.error}
          showLabel={false}
        />
        <StatusIndicator
          status={openai.status}
          label="OpenAI"
          error={openai.error}
          showLabel={false}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-4 px-3 py-1.5 bg-gray-50 rounded-lg border',
        className
      )}
    >
      <StatusIndicator
        status={database.status}
        label="Database"
        error={database.error}
      />
      <StatusIndicator status={openai.status} label="OpenAI" error={openai.error} />
    </div>
  );
};

// ============================================
// CONNECTION ERROR BANNER
// ============================================

interface ConnectionErrorBannerProps {
  onRetry?: () => void;
  onSetupP2P?: () => void;
}

/**
 * Full-width error banner shown when database is not connected
 * Prompts user to set up P2P sync
 */
export const ConnectionErrorBanner: React.FC<ConnectionErrorBannerProps> = ({
  onRetry,
  onSetupP2P,
}) => {
  const { database, checkAll } = useConnectionStore();
  const { init: initSync, initializing, initError, initialized } = useSyncStore();
  const [localInitializing, setLocalInitializing] = React.useState(false);

  // Don't show error banner while still initializing
  if (initializing || localInitializing) {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <div>
            <h3 className="font-medium text-blue-800">Initializing Database</h3>
            <p className="text-sm text-blue-700">Setting up local storage, please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't show error if database is connected or still checking
  if (database.status !== 'error' || database.status === 'checking') {
    return null;
  }

  // If initialized successfully, don't show error
  if (initialized && database.status !== 'error') {
    return null;
  }

  const handleRetry = async () => {
    setLocalInitializing(true);
    try {
      await initSync();
      checkAll();
      onRetry?.();
    } catch (err) {
      console.error('Database init failed:', err);
    } finally {
      setLocalInitializing(false);
    }
  };

  const handleSetupStorage = async () => {
    setLocalInitializing(true);
    try {
      await initSync();
      checkAll();
    } catch (err) {
      console.error('Database init failed:', err);
      // If init fails, open settings panel for manual setup
      onSetupP2P?.();
    } finally {
      setLocalInitializing(false);
    }
  };

  const isInitializing = initializing || localInitializing;

  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
      <div className="flex items-start gap-3">
        <span className="text-amber-500 text-lg">⚠️</span>
        <div className="flex-1">
          <h3 className="font-medium text-amber-800">Database Not Initialized</h3>
          <p className="text-sm text-amber-700 mt-1">
            {initError || 'Set up local storage to get started with CEO Command Center.'}
          </p>

          <div className="mt-3">
            {/* P2P Option */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
              <h4 className="font-medium text-blue-800 flex items-center gap-2">
                <span>🔄</span> Local-First Storage
              </h4>
              <p className="text-xs text-blue-700 mt-1">
                Store data locally in your browser with optional P2P sync to other
                devices. No server required.
              </p>
              <button
                onClick={handleSetupStorage}
                disabled={isInitializing}
                className="mt-2 w-full px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? 'Initializing...' : 'Set Up Local Storage'}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleRetry}
          disabled={isInitializing}
          className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 transition-colors disabled:opacity-50"
        >
          {isInitializing ? '...' : 'Retry'}
        </button>
      </div>
    </div>
  );
};

// ============================================
// EXPORT
// ============================================

export default ConnectionStatus;
